import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { paymentAPI } from '../api/paymentEndpoints';
import websocketService from '../services/websocketService';
import { showToast } from '../components/common/Toaster';
import { isAuthenticated } from '../utils/helpers';

const CourseOwnershipContext = createContext();

export const useCourseOwnership = () => {
  const context = useContext(CourseOwnershipContext);
  if (!context) {
    throw new Error('useCourseOwnership must be used within a CourseOwnershipProvider');
  }
  return context;
};

export const CourseOwnershipProvider = ({ children }) => {
  const location = useLocation();
  const [ownedCourses, setOwnedCourses] = useState([]); // Array of purchased course IDs
  const [purchasedCourses, setPurchasedCourses] = useState([]); // Full purchase data
  const [loading, setLoading] = useState(true);
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [userId, setUserId] = useState(null);
  
  const publicRoutes = ['/login', '/register', '/forgot-password', '/reset-password'];
  const isPublicRoute = publicRoutes.some(route => 
    location.pathname === route || location.pathname.startsWith(`${route}/`)
  );

  // Fetch all purchased courses
  const fetchMyCourses = useCallback(async () => {
    try {
      setLoading(true);
      const response = await paymentAPI.getMyCourses();
      const purchases = response.data?.purchases || [];
      setPurchasedCourses(purchases);
      // Extract course IDs for quick ownership checks
      const courseIds = purchases.map(p => p.course_id);
      setOwnedCourses(courseIds);
    } catch (error) {
      console.error('Failed to fetch purchased courses:', error);
      setOwnedCourses([]);
      setPurchasedCourses([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Check if user owns a specific course
  const checkCourseOwnership = useCallback(async (courseId) => {
    try {
      const response = await paymentAPI.checkCourseOwnership(courseId);
      return response.data?.owns_course || false;
    } catch (error) {
      console.error('Failed to check course ownership:', error);
      return false;
    }
  }, []);

  // Check if user owns a course (from cached data)
  const ownsCourse = useCallback((courseId) => {
    return ownedCourses.includes(courseId);
  }, [ownedCourses]);

  // Initialize course ownership check (skip on public routes or when not authenticated)
  useEffect(() => {
    if (!isAuthenticated() || isPublicRoute) {
      setLoading(false);
      return;
    }
    fetchMyCourses();
  }, [location.pathname, isPublicRoute, fetchMyCourses]);

  // Set up WebSocket listeners for payment updates
  useEffect(() => {
    if (userId) {
      websocketService.connect(userId);
      
      const handlePaymentCompleted = (data) => {
        if (data.status === 'success' && data.course_id) {
          // Add the newly purchased course to owned courses
          setOwnedCourses(prev => [...prev, data.course_id]);
          showToast('success', 'Course purchased successfully!');
          setShowPurchaseModal(false);
          // Refresh the full list
          fetchMyCourses();
        }
      };

      websocketService.on('payment_completed', handlePaymentCompleted);

      return () => {
        websocketService.off('payment_completed', handlePaymentCompleted);
      };
    }
  }, [userId, fetchMyCourses]);

  // Handle purchase required scenarios
  const handlePurchaseRequired = useCallback((course) => {
    setSelectedCourse(course);
    setShowPurchaseModal(true);
  }, []);

  // Handle successful purchase
  const handlePurchaseSuccess = useCallback((courseId) => {
    setOwnedCourses(prev => [...prev, courseId]);
    showToast('success', 'Course purchased successfully!');
    setShowPurchaseModal(false);
    fetchMyCourses();
  }, [fetchMyCourses]);

  // Purchase a course
  const purchaseCourse = useCallback(async (courseId, amount, msisdn) => {
    try {
      const response = await paymentAPI.purchaseCourse(courseId, amount, msisdn);
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.response?.data?.error || 'Failed to initiate purchase';
      throw new Error(errorMessage);
    }
  }, []);

  // Get purchase details for a course
  const getPurchaseDetails = useCallback((courseId) => {
    return purchasedCourses.find(p => p.course_id === courseId);
  }, [purchasedCourses]);

  const value = {
    ownedCourses,
    purchasedCourses,
    loading,
    showPurchaseModal,
    setShowPurchaseModal,
    selectedCourse,
    setSelectedCourse,
    setUserId,
    fetchMyCourses,
    checkCourseOwnership,
    ownsCourse,
    handlePurchaseRequired,
    handlePurchaseSuccess,
    purchaseCourse,
    getPurchaseDetails,
  };

  return (
    <CourseOwnershipContext.Provider value={value}>
      {children}
    </CourseOwnershipContext.Provider>
  );
};


